import { FormattedMessage } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Add, Create, Delete } from "@mui/icons-material";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { DeleteDialog } from "@dzambalaorg/mui-dialog-delete";
import { getEmptyTemplate } from "@dzambalaorg/mui-inputs-asset";
import { formatItem } from "@framework/exchange";
import { ListAction, ListActions, StyledListItem, StyledListWrapper, StyledPagination } from "@framework/styled";
import { DismantleStatus, DismantleStrategy, IDismantle, TokenType } from "@framework/types";
import { useDialogDelete, useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useGlobalDialogFormCallbacks } from "@framework/shared";
import { Button, FormGrid, List } from "@dzambalaorg/ui-kit";

import { AdminDialogKeys } from "../../../../../shared";
import { dismantleCreateFilter, dismantleEditFilter, prepareDismantleInitialValues } from "./schemas";
import { dismantleValidationSchema } from "./form/validation";

const DISMANTLE_URL = "/recipes/dismantle";
const emptyValues = {
  price: getEmptyTemplate(TokenType.ERC721),
  item: getEmptyTemplate(TokenType.ERC1155),
  dismantleStatus: DismantleStatus.ACTIVE,
  dismantleStrategy: DismantleStrategy.FLAT,
  rarityMultiplier: 0,
};

export const Dismantle = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount, refetch } = useFetchQuery<
    IPaginationResult<IDismantle>
  >(DISMANTLE_URL, { enableInfiniteScroll: isMd }, void 0, {
    dismantleStatus: [DismantleStatus.ACTIVE],
  });

  const { openDialog } = useGlobalDialogFormCallbacks<IDismantle, IDismantle>();
  const { handleDelete } = useDialogDelete<IDismantle>();

  const dismantleCreate = () => {
    openDialog(AdminDialogKeys.DISMANTLE_CREATE, emptyValues as IDismantle, {
      refetch,
      initialValues: emptyValues as IDismantle,
      message: "dialogs.create",
      testId: "DismantleFormDialog",
      filter: dismantleCreateFilter,
      url: DISMANTLE_URL,
      validationSchema: dismantleValidationSchema,
      componentProps: {
        mode: "create",
      },
    });
  };

  const dismantleEdit = (dismantle: IDismantle) => () => {
    openDialog(AdminDialogKeys.DISMANTLE_EDIT, dismantle, {
      refetch,
      initialValues: dismantle,
      message: "dialogs.edit",
      testId: "DismantleFormDialog",
      url: `${DISMANTLE_URL}/${dismantle.id}`,
      validationSchema: dismantleValidationSchema,
      filter: dismantleEditFilter,
      prepareInitialValues: prepareDismantleInitialValues,
      componentProps: {
        mode: "edit",
      },
    });
  };

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "recipes", "recipes.dismantle"]} />

      <PageHeader message="pages.recipes.dismantle.title">
        <Button intent="secondary" startIcon={<Add />} onClick={dismantleCreate} data-testid="DismantleCreateButton">
          <FormattedMessage id="form.buttons.create" />
        </Button>
      </PageHeader>

      <SearchWithFilters initialValues={{ dismantleStatus: [DismantleStatus.ACTIVE] }}>
        <FormGrid
          columns={[1]}
          containerProps={{ sx: theme => ({ marginTop: theme.spacing(theme.tokens.spacing.xl) }) }}
        >
          <SelectInput multiple name="dismantleStatus" options={DismantleStatus} />
        </FormGrid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(dismantle => (
            <StyledListItem key={dismantle.id}>
              <List.ItemText sx={{ flex: "0 1 45%" }}>{formatItem(dismantle.price)}</List.ItemText>
              <List.ItemText sx={{ flex: "0 1 45%" }}>{formatItem(dismantle.item)}</List.ItemText>
              <ListActions>
                <ListAction
                  onClick={dismantleEdit(dismantle)}
                  message="form.buttons.edit"
                  dataTestId="DismantleEditButton"
                  icon={Create}
                />
                <ListAction
                  onClick={handleDelete(dismantle)}
                  message="form.buttons.delete"
                  dataTestId="DismantleDeleteButton"
                  icon={Delete}
                />
              </ListActions>
            </StyledListItem>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <StyledPagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <DeleteDialog url={DISMANTLE_URL} refetch={refetch} />
    </Grid>
  );
};
